import React, { useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { format, parseISO } from "date-fns";
import { colors, fontSize, margins } from "style";
import Card from "components/Card";
import { DailyReportHeader, DailyReportResponse } from "components/DailyReport";
import { UserFullName } from "components/User";
import DataTable from "components/DataTable";
import { DAILY_REPORT_QUESTION_CATEGORIES } from "constants/index";

/**
 * DailyReportCard
 * Displays a single day's standup report with all questions and participant responses
 *
 * @param {string}    date            // The date for this report (YYYY-MM-DD)
 * @param {Object}    reports         // Report of all the participants with their responses of the asked questions
 * @param {boolean}   loading         // Whether the report is loading
 */
const DailyReportCard = ({ date, reports, loading, ...props }) => {
  const intl = useIntl();
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <StyledCard>
      <DailyReportHeader
        onClick={() => setIsExpanded(!isExpanded)}
        isExpanded={isExpanded}
        loading={loading}
        date={date}
        {...props}
      />
      {isExpanded && (
        <DataTable
          loading={loading}
          noResultsMessage={<FormattedMessage id="Squads.Squad.DailyReport.NoResults" />}
          data={reports?.map(({ standupEntries, ...rest }) => ({ ...rest, ...standupEntries }))}
          columns={COLUMN_DATA(reports?.[0]?.standupEntries, intl)}
        />
      )}
    </StyledCard>
  );
};

const COLUMN_DATA = (standupEntries = {}, intl) =>
  [
    {
      cell: ({ user, completedAt }) => (
        <UserFullName
          data={{
            avatarUrl: user?.imageUrl,
            firstName: user?.name || intl.formatMessage({ id: "Squads.Squad.DailyReport.Participant.Unknown" }),
            lastName: "",
          }}
          withPhoto
          photoProps={{ width: "5rem", height: "5rem" }}
        >
          {completedAt && (
            <SubmittedAt>
              <FormattedMessage
                id="Squads.Squad.DailyReport.Response.SubmittedAt"
                values={{ time: format(parseISO(completedAt), "h:mm a") }}
              />
            </SubmittedAt>
          )}
        </UserFullName>
      ),
    },
    ...Object.keys(standupEntries).map((entry) => {
      const { question, category } = standupEntries[entry] || {};
      const isBlocker = DAILY_REPORT_QUESTION_CATEGORIES[category] === DAILY_REPORT_QUESTION_CATEGORIES.IMPEDIMENT;

      return {
        label: <span css={isBlocker && blockerStyles}>{question}</span>,
        cell: (row) => <DailyReportResponse response={row[entry]} isBlocker={isBlocker} />,
      };
    }),
  ].filter(Boolean);

const StyledCard = styled(Card)`
  margin-bottom: ${margins.normal};
  overflow: visible;
  padding-top: 1rem;
`;

const SubmittedAt = styled.div`
  font-size: ${fontSize.xxsmall};
  color: ${colors.grayAnatomyLight1};
  margin-top: 0.5rem;
`;

const blockerStyles = css`
  color: ${colors.red};
`;

DailyReportCard.propTypes = {
  date: PropTypes.string.isRequired,
  reports: PropTypes.arrayOf(
    PropTypes.shape({
      completedAt: PropTypes.string,
      status: PropTypes.string,
      user: PropTypes.shape({
        name: PropTypes.string,
        imageUrl: PropTypes.string,
      }),
      date: PropTypes.string.isRequired,
      standupEntries: PropTypes.arrayOf(
        PropTypes.shape({
          question: PropTypes.string.isRequired,
          answer: PropTypes.string,
          category: PropTypes.string,
          optional: PropTypes.bool,
        })
      ),
    })
  ),
  loading: PropTypes.bool,
};

export default DailyReportCard;
